import { useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import Button from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import { FOOTBALL_MARKET_NEW_SIGNINGS } from '@sorare/core/src/constants/__generated__/routes';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useOneTimeDialogContext } from '@sorare/core/src/contexts/oneTimeDialog';
import { OneTimeDialog } from '@sorare/core/src/contexts/oneTimeDialog/Provider';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { sortBy } from '@sorare/core/src/lib/arrays';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { earlyAccessEvents } from '@sorare/marketplace/src/lib/earlyAccess';

import challengerHeader from './assets/challenger-header.svg';
import challengerLeagues from './assets/challenger-leagues.png';
import championHeader from './assets/champion-header.svg';
import championLeagues from './assets/champion-leagues.png';
import contenderHeader from './assets/contender-header.png';
import contenderLeagues from './assets/contender-leagues.png';

const earlyAccessDropping = `${FRONTEND_ASSET_HOST}/videos/early_access_dropping_vertical.mp4`;

const Wrapper = styled.div`
  --c-dialog-background: transparent;
`;

const Actions = styled(Vertical)`
  padding: var(--unit);
`;

const LaunchedRoot = styled(Vertical)`
  background-color: var(--c-neutral-100);
  border-radius: var(--double-unit);
  overflow: hidden;
`;
const Header = styled.div`
  --header-width: 280px;

  position: absolute;
  top: 0;
  left: calc(50% - var(--header-width) / 2);
  text-align: center;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: top center;
  padding: var(--unit);
  height: 175px;
  width: var(--header-width);
`;

const AnnouncementTitle = styled(HeadlineL)`
  min-height: 70px;
`;

const BackgroundVideo = styled.video`
  object-fit: cover;
  object-position: center bottom;
  height: 370px;
  @media ${tabletAndAbove} {
    height: 480px;
  }
`;

const IMAGES = {
  challenger: { header: challengerHeader, leagues: challengerLeagues },
  champion: { header: championHeader, leagues: championLeagues },
  contender: { header: contenderHeader, leagues: contenderLeagues },
} as const;

export const EarlyAccessAnnouncement = () => {
  const {
    flags: { useEnableEarlyAccessFlows = false },
  } = useFeatureFlags();

  const now = useMemo(() => new Date(), []);

  // find current event to display, the most recently started
  const eventsSortedByStartDateDesc = sortBy(
    e => e.startDate,
    [...earlyAccessEvents]
  ).reverse();
  const event = eventsSortedByStartDateDesc.find(e => e.startDate < now)!;
  const { lifecycle } = event;

  const { onboarded } = useCurrentUserContext();
  // Use a ref as we dont want update it so it wont show during the whole first session
  const isOnboarded = useRef(onboarded[Sport.FOOTBALL]);

  const { sawDialog } = useOneTimeDialogContext();
  const alreadySeen = sawDialog(lifecycle);

  const { leagues, header } = IMAGES[event.slug];

  if (!isOnboarded.current || !useEnableEarlyAccessFlows || alreadySeen) {
    return null;
  }

  return (
    <Wrapper>
      <OneTimeDialog dialogId={lifecycle} show={!alreadySeen}>
        {({ onClose, open }) => (
          <Dialog
            maxWidth="xs"
            fullWidth
            hideHeader
            transparent
            open={open}
            onClose={onClose}
            fullScreen={false}
          >
            <Vertical>
              <LaunchedRoot>
                <BackgroundVideo muted playsInline autoPlay>
                  <source src={earlyAccessDropping} />
                </BackgroundVideo>

                <Header style={{ backgroundImage: `url(${header})` }}>
                  <Vertical center>
                    <AnnouncementTitle>
                      <FormattedMessage
                        defaultMessage="New {leagues} cards are here!"
                        id="earlyStartedAccessAnnouncement.leagues.title"
                        values={{
                          leagues: event.displayName,
                        }}
                      />
                    </AnnouncementTitle>
                    <img src={leagues} alt="" />
                  </Vertical>
                </Header>
              </LaunchedRoot>
              <Actions center gap={2}>
                <Button
                  fullWidth
                  color="secondary"
                  size="medium"
                  onClick={onClose}
                  to={`${FOOTBALL_MARKET_NEW_SIGNINGS}?${buildFilterQuery({ [event.searchParams]: true })}`}
                >
                  <FormattedMessage
                    id="forYou.earlyAccess.viewCards"
                    defaultMessage="Discover new cards"
                  />
                </Button>
                <Button size="medium" color="transparent" onClick={onClose}>
                  <FormattedMessage {...glossary.doItLater} />
                </Button>
              </Actions>
            </Vertical>
          </Dialog>
        )}
      </OneTimeDialog>
    </Wrapper>
  );
};
